import { useState, useEffect, useCallback } from 'react';
import {
  Database, RefreshCw, CheckCircle, XCircle, AlertTriangle, Clock,
  ChevronDown, ChevronRight, Play, Zap, Calendar, Server, ArrowDown,
  Loader2,
} from 'lucide-react';
import { colors, Card } from '../../shared';
import { useData } from '../../../contexts/DataContext';
import { useAuth } from '../../../contexts/AuthContext';
import { triggerSync } from '../../../utils/appfolioApi';

// ─── Helpers ───────────────────────────────────────────────────────────────

function timeAgo(timestamp) {
  if (!timestamp) return 'Never';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);

  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
}

function getFreshnessStatus(timestamp, thresholds) {
  if (!timestamp) return { color: colors.textMuted, label: 'No data', level: 'none' };
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  const minutes = (Date.now() - date.getTime()) / (1000 * 60);

  if (minutes < thresholds.green) return { color: colors.green, label: 'Fresh', level: 'green' };
  if (minutes < thresholds.yellow) return { color: colors.orange, label: 'Stale', level: 'yellow' };
  return { color: colors.red, label: 'Outdated', level: 'red' };
}

const FreshnessIndicator = ({ status }) => (
  <span
    className="inline-block w-2.5 h-2.5 rounded-full"
    style={{ background: status.color }}
    title={status.label}
  />
);

// ─── Section 1: Sync Status Dashboard ──────────────────────────────────────

const SyncStatusCard = ({ title, icon: Icon, timestamp, status, counts, thresholds }) => {
  const freshness = getFreshnessStatus(timestamp, thresholds);

  return (
    <Card className="p-4 flex-1 min-w-[200px]">
      <div className="flex items-center gap-2 mb-3">
        <Icon size={18} style={{ color: colors.yellow }} />
        <span className="font-semibold text-sm" style={{ color: colors.textPrimary }}>{title}</span>
        <FreshnessIndicator status={freshness} />
      </div>

      <div className="space-y-2">
        <div className="flex justify-between text-xs">
          <span style={{ color: colors.textMuted }}>Last run</span>
          <span style={{ color: colors.textSecondary }}>{timeAgo(timestamp)}</span>
        </div>

        <div className="flex justify-between text-xs">
          <span style={{ color: colors.textMuted }}>Status</span>
          <span style={{
            color: status === 'success' ? colors.green : status === 'error' ? colors.red : colors.textMuted
          }}>
            {status || 'N/A'}
          </span>
        </div>

        {counts && Object.keys(counts).length > 0 && (
          <div className="pt-2 border-t" style={{ borderColor: colors.border }}>
            {Object.entries(counts).map(([key, val]) => (
              <div key={key} className="flex justify-between text-xs">
                <span style={{ color: colors.textMuted }}>{key.replace(/_/g, ' ')}</span>
                <span style={{ color: colors.textSecondary }}>{val?.toLocaleString()}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
};

const SyncStatusDashboard = ({ syncStatus }) => {
  if (!syncStatus) {
    return (
      <Card className="p-6 text-center">
        <Database size={32} className="mx-auto mb-3" style={{ color: colors.textMuted }} />
        <p style={{ color: colors.textMuted }}>No sync data yet. Run a seed sync to get started.</p>
      </Card>
    );
  }

  return (
    <div className="flex gap-3 flex-wrap">
      <SyncStatusCard
        title="Frequent (15m)"
        icon={Zap}
        timestamp={syncStatus.lastFrequentSyncAt}
        status={syncStatus.frequentSyncStatus}
        counts={syncStatus.frequentSyncCounts}
        thresholds={{ green: 20, yellow: 60 }}
      />
      <SyncStatusCard
        title="Daily (2am CT)"
        icon={Calendar}
        timestamp={syncStatus.lastDailySyncAt}
        status={syncStatus.dailySyncStatus}
        counts={syncStatus.dailySyncCounts}
        thresholds={{ green: 1560, yellow: 2880 }}
      />
      <SyncStatusCard
        title="Seed (Manual)"
        icon={Server}
        timestamp={syncStatus.lastSeedAt}
        status={syncStatus.seedSyncStatus}
        counts={syncStatus.seedSyncCounts}
        thresholds={{ green: 43200, yellow: 86400 }}
      />
    </div>
  );
};

// ─── Section 2: Architecture Diagram ───────────────────────────────────────

const ArchitectureDiagram = () => (
  <Card className="p-5">
    <h3 className="text-sm font-semibold mb-4" style={{ color: colors.textPrimary }}>
      Sync Architecture
    </h3>
    <div className="space-y-3">
      {/* AppFolio API */}
      <div className="text-center">
        <div className="inline-block px-4 py-2 rounded-lg text-xs font-medium"
          style={{ background: colors.yellowDim, color: colors.yellow, border: `1px solid ${colors.yellow}40` }}>
          AppFolio Reports API
        </div>
      </div>

      <div className="flex justify-center">
        <ArrowDown size={16} style={{ color: colors.textMuted }} />
      </div>

      {/* Sync Cycles */}
      <div className="flex gap-3 justify-center flex-wrap">
        <div className="px-3 py-2 rounded-lg text-xs" style={{ background: colors.inputBg, border: `1px solid ${colors.border}` }}>
          <div className="font-medium mb-1" style={{ color: colors.green }}>Every 15 min</div>
          <div style={{ color: colors.textMuted }}>Active WOs</div>
          <div style={{ color: colors.textMuted }}>Labor (3-day)</div>
        </div>
        <div className="px-3 py-2 rounded-lg text-xs" style={{ background: colors.inputBg, border: `1px solid ${colors.border}` }}>
          <div className="font-medium mb-1" style={{ color: colors.orange }}>Daily 2am CT</div>
          <div style={{ color: colors.textMuted }}>Completed WOs</div>
          <div style={{ color: colors.textMuted }}>Labor (30-day)</div>
          <div style={{ color: colors.textMuted }}>Surveys, Props</div>
          <div style={{ color: colors.textMuted }}>Units, Inventory</div>
          <div style={{ color: colors.textMuted }}>Technicians</div>
        </div>
      </div>

      <div className="flex justify-center">
        <ArrowDown size={16} style={{ color: colors.textMuted }} />
      </div>

      {/* Firestore */}
      <div className="text-center">
        <div className="inline-block px-4 py-2 rounded-lg text-xs font-medium"
          style={{ background: colors.greenDim, color: colors.green, border: `1px solid ${colors.green}40` }}>
          Firestore Collections
        </div>
      </div>

      <div className="flex justify-center">
        <ArrowDown size={16} style={{ color: colors.textMuted }} />
      </div>

      {/* Browser */}
      <div className="text-center">
        <div className="inline-block px-4 py-2 rounded-lg text-xs font-medium"
          style={{ background: colors.inputBg, color: colors.textSecondary, border: `1px solid ${colors.border}` }}>
          Browser reads Firestore
        </div>
      </div>
    </div>
  </Card>
);

// ─── Section 3: Sync Config Preview ────────────────────────────────────────

const ConfigSection = ({ title, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className="border-b" style={{ borderColor: colors.border }}>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 w-full py-3 px-4 text-left"
      >
        {open ? <ChevronDown size={14} style={{ color: colors.textMuted }} /> : <ChevronRight size={14} style={{ color: colors.textMuted }} />}
        <span className="text-sm font-medium" style={{ color: colors.textPrimary }}>{title}</span>
      </button>
      {open && (
        <div className="px-4 pb-4">
          <pre className="text-xs p-3 rounded-lg overflow-x-auto" style={{ background: colors.inputBg, color: colors.textSecondary }}>
            {children}
          </pre>
        </div>
      )}
    </div>
  );
};

const SyncConfigPreview = () => (
  <Card className="overflow-hidden">
    <div className="px-4 py-3 border-b" style={{ borderColor: colors.border }}>
      <h3 className="text-sm font-semibold" style={{ color: colors.textPrimary }}>Sync Configuration</h3>
    </div>

    <ConfigSection title="Frequent Sync (Active WOs + Labor)">
{`// Active WO status codes:
work_order_statuses: ['0','10','1','2','9','11','3','6','8','12']
status_date: 'all'

// Labor: 3-day rolling window
labor_performed_from: (today - 3 days)
labor_performed_to: today

// Collections: work_orders, labor_entries
// Doc IDs: work_order_id, labor_detail_id
// Strategy: merge (upsert)`}
    </ConfigSection>

    <ConfigSection title="Daily Sync (Completed + Full Refresh)">
{`// Completed/Canceled WO status codes:
work_order_statuses: ['4','5','7']
status_date: '4' (custom range)
status_date_range_from: (today - 1 day)

// Labor: 30-day rolling window
// Surveys: survey_type = 'maintenance'
// Properties: full property_directory
// Units: full unit_directory
// Inventory: full inventory_usage
// Technicians: extracted from WO + labor data

// Safety net: if lastDailySyncAt > 3 days, widens to 30 days`}
    </ConfigSection>

    <ConfigSection title="Seed Sync (Full 2-Year Pull)">
{`// Step 1: Delete all docs in managed collections
// Step 2: Fetch 2 years of everything
// Step 3: Write with AppFolio IDs as doc IDs

// Rate limit: 2.2s between initial API requests
// Pagination: next_page_url (not rate-limited)

// Collections cleared: work_orders, labor_entries,
//   surveys, properties, units, inventory, technicians`}
    </ConfigSection>
  </Card>
);

// ─── Section 4: Manual Controls ────────────────────────────────────────────

const ManualControls = () => {
  const { user } = useAuth();
  const isGodMode = user?.email === 'aj@snshn.co';
  const [running, setRunning] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [confirmSeed, setConfirmSeed] = useState(false);

  const handleSync = useCallback(async (action) => {
    setRunning(action);
    setResult(null);
    setError(null);
    setConfirmSeed(false);

    try {
      const res = await triggerSync(action);
      setResult(res);
    } catch (err) {
      setError(err.message);
    } finally {
      setRunning(null);
    }
  }, []);

  return (
    <Card className="p-5">
      <h3 className="text-sm font-semibold mb-4" style={{ color: colors.textPrimary }}>
        Manual Sync Controls
      </h3>

      <div className="flex gap-3 flex-wrap">
        <button
          onClick={() => handleSync('frequent')}
          disabled={!!running}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all"
          style={{
            background: running === 'frequent' ? colors.yellowDim : colors.inputBg,
            color: running === 'frequent' ? colors.yellow : colors.textPrimary,
            border: `1px solid ${colors.border}`,
            opacity: running && running !== 'frequent' ? 0.5 : 1,
          }}
        >
          {running === 'frequent' ? <Loader2 size={14} className="animate-spin" /> : <Zap size={14} />}
          Run Frequent
        </button>

        <button
          onClick={() => handleSync('daily')}
          disabled={!!running}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all"
          style={{
            background: running === 'daily' ? colors.yellowDim : colors.inputBg,
            color: running === 'daily' ? colors.yellow : colors.textPrimary,
            border: `1px solid ${colors.border}`,
            opacity: running && running !== 'daily' ? 0.5 : 1,
          }}
        >
          {running === 'daily' ? <Loader2 size={14} className="animate-spin" /> : <Calendar size={14} />}
          Run Daily
        </button>

        {isGodMode && (
          !confirmSeed ? (
            <button
              onClick={() => setConfirmSeed(true)}
              disabled={!!running}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all"
              style={{
                background: colors.redDim,
                color: colors.red,
                border: `1px solid ${colors.red}40`,
                opacity: running ? 0.5 : 1,
              }}
            >
              <Server size={14} />
              Run Seed (Full)
            </button>
          ) : (
            <div className="flex items-center gap-2">
              <span className="text-xs" style={{ color: colors.red }}>Delete all & re-sync?</span>
              <button
                onClick={() => handleSync('seed')}
                disabled={!!running}
                className="px-3 py-1.5 rounded-lg text-xs font-medium"
                style={{ background: colors.red, color: colors.black }}
              >
                {running === 'seed' ? 'Running...' : 'Confirm'}
              </button>
              <button
                onClick={() => setConfirmSeed(false)}
                className="px-3 py-1.5 rounded-lg text-xs font-medium"
                style={{ background: colors.inputBg, color: colors.textMuted }}
              >
                Cancel
              </button>
            </div>
          )
        )}
      </div>

      {/* Result display */}
      {result && (
        <div className="mt-4 p-3 rounded-lg text-xs" style={{
          background: result.async ? colors.yellowDim : colors.greenDim,
          color: result.async ? colors.yellow : colors.green,
        }}>
          <div className="flex items-center gap-2 mb-2">
            {result.async ? <Clock size={14} /> : <CheckCircle size={14} />}
            <span className="font-medium">
              {result.async ? result.message : `Sync completed: ${result.action}`}
            </span>
          </div>
          {result.counts && (
            <div className="space-y-1">
              {Object.entries(result.counts).map(([key, val]) => (
                <div key={key} className="flex justify-between">
                  <span>{key.replace(/_/g, ' ')}</span>
                  <span>{val?.toLocaleString()}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="mt-4 p-3 rounded-lg text-xs" style={{ background: colors.redDim, color: colors.red }}>
          <div className="flex items-center gap-2">
            <XCircle size={14} />
            <span>{error}</span>
          </div>
        </div>
      )}
    </Card>
  );
};

// ─── Section 5: Collection Health ──────────────────────────────────────────

const CollectionHealth = ({ syncStatus }) => {
  const { workOrders, laborEntries, satisfactionScores, properties, units, partsUsage, technicians } = useData();

  const collections = [
    { name: 'work_orders', count: workOrders.length, syncCount: syncStatus?.frequentSyncCounts?.work_orders || syncStatus?.dailySyncCounts?.work_orders },
    { name: 'labor_entries', count: laborEntries.length, syncCount: syncStatus?.frequentSyncCounts?.labor_entries || syncStatus?.dailySyncCounts?.labor_entries },
    { name: 'surveys', count: satisfactionScores.length, syncCount: syncStatus?.dailySyncCounts?.surveys },
    { name: 'properties', count: properties.length, syncCount: syncStatus?.dailySyncCounts?.properties },
    { name: 'units', count: units.length, syncCount: syncStatus?.dailySyncCounts?.units },
    { name: 'inventory', count: partsUsage.length, syncCount: syncStatus?.dailySyncCounts?.inventory },
    { name: 'technicians', count: technicians.length, syncCount: syncStatus?.dailySyncCounts?.technicians },
  ];

  // Determine freshness from last sync times
  const lastSync = syncStatus?.lastFrequentSyncAt || syncStatus?.lastDailySyncAt;
  const freshness = getFreshnessStatus(lastSync, { green: 20, yellow: 60 });

  return (
    <Card className="overflow-hidden">
      <div className="px-4 py-3 border-b" style={{ borderColor: colors.border }}>
        <h3 className="text-sm font-semibold" style={{ color: colors.textPrimary }}>Collection Health</h3>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr style={{ background: colors.inputBg }}>
              <th className="text-left px-4 py-2 font-medium" style={{ color: colors.textMuted }}>Collection</th>
              <th className="text-right px-4 py-2 font-medium" style={{ color: colors.textMuted }}>Records</th>
              <th className="text-right px-4 py-2 font-medium" style={{ color: colors.textMuted }}>Last Sync Count</th>
              <th className="text-center px-4 py-2 font-medium" style={{ color: colors.textMuted }}>Freshness</th>
            </tr>
          </thead>
          <tbody>
            {collections.map(col => (
              <tr key={col.name} className="border-b" style={{ borderColor: colors.border }}>
                <td className="px-4 py-2.5 font-medium" style={{ color: colors.textPrimary }}>
                  {col.name.replace(/_/g, ' ')}
                </td>
                <td className="px-4 py-2.5 text-right" style={{ color: colors.textSecondary }}>
                  {col.count.toLocaleString()}
                </td>
                <td className="px-4 py-2.5 text-right" style={{ color: colors.textMuted }}>
                  {col.syncCount != null ? col.syncCount.toLocaleString() : '—'}
                </td>
                <td className="px-4 py-2.5 text-center">
                  <FreshnessIndicator status={col.count > 0 ? freshness : { color: colors.textMuted, label: 'Empty' }} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {lastSync && (
        <div className="px-4 py-2 text-xs" style={{ color: colors.textMuted, background: colors.inputBg }}>
          Last sync: {timeAgo(lastSync)}
        </div>
      )}
    </Card>
  );
};

// ─── Main Component ────────────────────────────────────────────────────────

export default function DataTab() {
  const { syncStatus } = useData();

  return (
    <div className="p-6 space-y-6 max-w-5xl">
      <div>
        <h2 className="text-lg font-bold" style={{ color: colors.textPrimary }}>Data Sync</h2>
        <p className="text-sm mt-1" style={{ color: colors.textMuted }}>
          AppFolio data is synced into Firestore by Cloud Functions. The browser reads Firestore only.
        </p>
      </div>

      {/* Sync Status Cards */}
      <div>
        <h3 className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: colors.textMuted }}>
          Sync Status
        </h3>
        <SyncStatusDashboard syncStatus={syncStatus} />
      </div>

      {/* Architecture + Manual Controls side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ArchitectureDiagram />
        <ManualControls />
      </div>

      {/* Collection Health */}
      <CollectionHealth syncStatus={syncStatus} />

      {/* Sync Config */}
      <SyncConfigPreview />
    </div>
  );
}
